import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Card, Typography, Alert, message } from 'antd';
import { UserOutlined, LockOutlined, HeartOutlined, MailOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';
import authService from '../../services/authService';
import './AuthPages.scss';

const { Title, Text } = Typography;

const LoginPage = observer(() => {
  const navigate = useNavigate();
  const { authStore } = useStores();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Email verification state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resending, setResending] = useState(false);

  const onFinish = async (values) => {
    setEmailNotVerified(false);
    const result = await authStore.login(values.email, values.password);

    if (result.success) {
      navigate('/');
    } else if (result.emailNotVerified) {
      setEmailNotVerified(true);
      setUnverifiedEmail(result.email);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await authService.resendVerificationEmail(unverifiedEmail);
      message.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      message.error('Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card-wrapper bounce-in">
          <Card className="auth-card">
            <div className="auth-header">
              <div className="logo-section">
                <HeartOutlined className="logo-icon" />
                <Title level={2} className="auth-title">
                  {t('auth.login.title')}
                </Title>
                <Text className="auth-subtitle">
                  {t('auth.login.subtitle')}
                </Text>
              </div>
            </div>

            {emailNotVerified && (
              <Alert
                message={t('auth.login.emailNotVerified')}
                description={
                  <div>
                    <p>{t('auth.login.emailNotVerifiedDesc')}</p>
                    <Button
                      type="link"
                      icon={<MailOutlined />}
                      onClick={handleResendVerification}
                      loading={resending}
                      style={{ padding: 0 }}
                    >
                      {t('auth.login.resendVerification')}
                    </Button>
                  </div>
                }
                type="warning"
                showIcon
                closable
                onClose={() => setEmailNotVerified(false)}
                style={{ marginBottom: 24 }}
              />
            )}

            {authStore.error && !emailNotVerified && (
              <Alert
                message={authStore.error}
                type="error"
                showIcon
                closable
                onClose={() => authStore.clearError()}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label={t('auth.login.emailLabel')}
                rules={[
                  { required: true, message: t('auth.login.emailRequired') },
                  { type: 'email', message: t('auth.login.emailInvalid') },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t('auth.login.emailPlaceholder')}
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('auth.login.passwordLabel')}
                rules={[{ required: true, message: t('auth.login.passwordRequired') }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  autoComplete="current-password"
                />
              </Form.Item>

              <div style={{ textAlign: 'right', marginBottom: 16 }}>
                <Link to="/forgot-password" className="auth-link">
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={authStore.isLoading}
                  block
                  className="auth-button"
                >
                  {t('auth.login.submitButton')}
                </Button>
              </Form.Item>

              <div className="auth-footer">
                <Text>{t('auth.login.noAccount')} </Text>
                <Link to="/register" className="auth-link">
                  {t('auth.login.createAccount')}
                </Link>
              </div>
            </Form>
          </Card>

          <div className="auth-decoration">
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LoginPage;
